import {
    getSafeOrDefault,
    isNumber
} from "../../../services/common/object-util";
import SportCodes from "../../common/sport-codes";
import FlagFootballTransitionConfig from "../../flag-football/state/flag-football-play-transtions";
import GoalPost from "../enums/goal-post";
import Ruleset from "../enums/ruleset";
import FootballTransitionConfig from "../state/football-play-transitions";
import FootballState from "../state/football-state";
import FootballTransition from "../state/football-transition";

export const secondHalfPeriodNumber = (context) =>
    context.gameConfig.general.periodsInRegulation / 2 + 1;

export const getTimeoutsByPeriod = (teamId, periodNumber, context) => {
    return getSafeOrDefault(() => context.timeouts[periodNumber][teamId], 0);
};

export const getTimeoutsByHalf = (teamId, halfNumber, context) => {
    let timeouts = 0;
    const secondHalfPrdNumber = secondHalfPeriodNumber(context);
    if (halfNumber <= 2) {
        const start = halfNumber === 1 ? 1 : secondHalfPrdNumber;
        const end =
            halfNumber === 1
                ? secondHalfPrdNumber
                : context.gameConfig.general.periodsInRegulation + 1;

        for (let prd = start; prd < end; prd++) {
            timeouts += getSafeOrDefault(
                () => context.timeouts[prd][teamId],
                0
            );
        }
    }
    return timeouts;
};

export const halfNumberByPeriod = (period, context) => {
    if (period <= context.gameConfig.general.periodsInRegulation) {
        return period < secondHalfPeriodNumber(context) ? 1 : 2;
    }
    return null;
};

export const getFieldDirectionMultiplier = (context) => {
    if (context.homeTeamHasPossession) {
        return 1;
    }
    return -1;
};

export const getYardsToFirstDown = (context) => {
    return (
        (context.firstDownAt - context.fieldPosition) *
        getFieldDirectionMultiplier(context)
    );
};

export const getYardsToFirstDownFriendly = (context, gameConfig) => {
    if (
        context.firstDownAt === 0 ||
        context.firstDownAt === gameConfig.general.fieldLength
    ) {
        return "GOAL";
    }
    return getYardsToFirstDown(context);
};

/***
 * Compute elapsed time between two game-clock points.
 * `startTime` and `endTime` are milliseconds on the game clock.
 * `minutesByPeriod` is whole minutes per period.
 *
 * Within a single period the calculation is direction-agnostic so it works
 * for both DESC (countdown) and ASC (countup) clocks. When the points span
 * periods the formula assumes a DESC clock (the production default), and
 * accounts for the periods crossed.
 */
export const getTimeDifference = (
    startTime,
    startPeriod,
    endTime,
    endPeriod,
    minutesByPeriod
) => {
    if (
        !isNumber(startTime) ||
        !isNumber(startPeriod) ||
        !isNumber(endTime) ||
        !isNumber(endPeriod) ||
        !isNumber(minutesByPeriod)
    ) {
        return 0;
    }
    if (startPeriod === endPeriod) {
        return Math.abs(startTime - endTime);
    }
    const periodLengthMs = minutesByPeriod * 60 * 1000;
    const periodOffsetMs = (endPeriod - startPeriod) * periodLengthMs;
    return Math.max(0, startTime - endTime + periodOffsetMs);
};

const isPlayerInOffense = (player, gameContext) => {
    return gameContext.playerInPossession?.teamId === player?.teamId;
};

const isPositionInOwnEndZone = (fieldPosition, gameContext) => {
    return gameContext.homeTeamHasPossession
        ? fieldPosition <= 0
        : fieldPosition >= gameContext.gameConfig.general.fieldLength;
};

export const getSafetyTacklingPlayer = (play, safetyFragment) => {
    const previousFragment = play.getPreviousFragment(safetyFragment);

    const { tackledBy, sackedBy } = previousFragment;
    const tacklingPlayers = tackledBy || sackedBy;

    return Array.isArray(tacklingPlayers) && tacklingPlayers.length
        ? tacklingPlayers[0]
        : null;
};

export const getSafetyRecoveringPlayer = (play, safetyFragment) => {
    const previousFragment = play.getPreviousFragment(safetyFragment);

    const { recoveredBy, continuedBy } = previousFragment;
    const recoveringPlayer = recoveredBy || continuedBy;

    return recoveringPlayer;
};

const isStateFromTransition = (associatedState, transitionName, play) => {
    const configurationMap = {
        [SportCodes.Football]: FootballTransitionConfig,
        [SportCodes.FlagFootball]: FlagFootballTransitionConfig
    };
    const config = configurationMap[play.getSportCode()];

    if (config) {
        const { from } = config.find((t) => t.name === transitionName) || {
            from: []
        };
        const states = Array.isArray(from) ? from : [from];
        return states.some((state) => state === associatedState);
    }
    return false;
};

/**
 * Determine if punting is allowed for the given ruleset
 * Indoor Football does not allow punting
 * @param {string} ruleSetId
 * @returns {boolean}
 */
export const isPuntAllowed = (ruleSetId) => {
    return ruleSetId !== Ruleset.IndoorFootball.code;
};

/**
 * Determine if Deuce is eligible for the given ruleset and play state
 * Deuce is only available in Indoor Football from the KickOff state
 * @param {string} ruleSetId
 * @param {FootballPlay} play
 * @returns {boolean}
 */
export const isDeuceEligible = (ruleSetId, play) => {
    // Early return if not Indoor Football - no need to check play state
    if (ruleSetId !== Ruleset.IndoorFootball.code) {
        return false;
    }

    // Safety check for play object
    if (!play || typeof play.historyContains !== "function") {
        return false;
    }

    // Deuce is available from KickOff state before any other kick outcome
    const isKickOffState =
        play.historyContains(FootballState.KickOff) &&
        !play.historyContains(FootballState.Deuce) &&
        !play.historyContains(FootballState.Returned) &&
        !play.historyContains(FootballState.TouchBack) &&
        !play.historyContains(FootballState.FairCatch) &&
        !play.historyContains(FootballState.OutOfBounds);

    return isKickOffState;
};

/**
 * Determine if Rebound Net (Hit Rebound Net) is eligible for the given ruleset
 * Rebound Net is only available in Indoor Football
 * @param {string} ruleSetId
 * @returns {boolean}
 */
export const isReboundNetEligible = (ruleSetId) => {
    return ruleSetId === Ruleset.IndoorFootball.code;
};

/**
 * Determine if Bar Ball (Hit Goalpost) is eligible for the given ruleset and play state
 * Bar Ball is available in Indoor Football from KickOff, FieldGoal, or PatKick states
 * @param {string} ruleSetId
 * @param {FootballPlay} play
 * @returns {boolean}
 */
export const isBarBallEligible = (ruleSetId, play) => {
    // Early return if not Indoor Football
    if (ruleSetId !== Ruleset.IndoorFootball.code) {
        return false;
    }

    // Safety check for play object
    if (!play || typeof play.historyContains !== "function") {
        return false;
    }

    // Already hit the goalpost or rebound net? Not eligible
    if (
        play.historyContains(FootballState.HitGoalpost) ||
        play.historyContains(FootballState.HitReboundNet)
    ) {
        return false;
    }

    // Check if we're in a valid kick state
    const isKickOff = play.historyContains(FootballState.KickOff);
    const isFieldGoal = play.historyContains(FootballState.FieldGoal);
    const isPatKick = play.historyContains(FootballState.PatKick);

    if (!isKickOff && !isFieldGoal && !isPatKick) {
        return false;
    }

    // For KickOff, exclude other kickoff-specific outcomes
    if (isKickOff) {
        return !(
            play.historyContains(FootballState.Deuce) ||
            play.historyContains(FootballState.Returned) ||
            play.historyContains(FootballState.TouchBack) ||
            play.historyContains(FootballState.FairCatch) ||
            play.historyContains(FootballState.OutOfBounds)
        );
    }

    // For FieldGoal/PatKick, check we haven't already resolved the kick
    return !(
        play.historyContains(FootballState.FieldGoalGood) ||
        play.historyContains(FootballState.FieldGoalMissed) ||
        play.historyContains(FootballState.PatCompleted)
    );
};

export const isCflKickEligible = (ruleSetId, play) => {
    const isCfl = ruleSetId === Ruleset.Cfl.code;
    const isRushOrPass = [
        FootballState.Rush,
        FootballState.Run,
        FootballState.Pass
    ].some((state) => play.historyContains(state));
    const isKickReturnPlay =
        [
            FootballState.Punt,
            FootballState.KickOff,
            FootballState.FieldGoal
        ].some((state) => play.historyContains(state)) &&
        [FootballState.Returned, FootballState.NoRecoveryKickReturn].some(
            (state) => play.historyContains(state)
        );

    return isCfl && (isRushOrPass || isKickReturnPlay);
};

export const isFieldGoalInelligible = (ruleSetId, play) => {
    const isFumbledSnap = play.historyContains(FootballState.FumbledSnap);
    const isCfl = ruleSetId === Ruleset.Cfl.code;

    return !isCfl && isFumbledSnap;
};

export const getPenaltyTeamIds = (play) => {
    const { homeTeamHasPossession, homeTeamId, awayTeamId } =
        play.contextSnapshot || {};
    return {
        offenseTeamId: homeTeamHasPossession ? homeTeamId : awayTeamId,
        defenseTeamId: homeTeamHasPossession ? awayTeamId : homeTeamId
    };
};

export const isDefensivePenalty = (play, penaltyFragment) => {
    const { offenseTeamId } = getPenaltyTeamIds(play);
    return penaltyFragment.team?.id !== offenseTeamId;
};

/**
 * A play wiped out by an accepted penalty ("No Play") never happened, so
 * anything it did to possession, the drive and the down has to be undone.
 * @param {FootballPlay} play
 * @returns {boolean}
 */
export const isPlayNegated = (play) =>
    play.historyContains(FootballState.NoPlay) ||
    play.historyContains(FootballState.PlayPenaltyNoPlay);

export const isPenaltyRestoringPossession = (
    play,
    penaltyFragment,
    context
) => {
    const { homeTeamHasPossession } = play.contextSnapshot || {};
    const isTurnover = context.homeTeamHasPossession !== homeTeamHasPossession;

    // A negated play hands the ball back to whoever had it, whatever happened
    // during the play and whichever team was penalised — e.g. contacting the
    // kicker wipes out a punt and the kicking team keeps possession.
    if (isPlayNegated(play)) {
        return isTurnover;
    }

    const isVoluntaryKick =
        play.historyContains(FootballState.KickOff) ||
        play.historyContains(FootballState.Punt) ||
        play.historyContains(FootballState.CflKick) ||
        play.historyContains(FootballState.FieldGoalMissed);
    if (isVoluntaryKick) {
        return false;
    }
    return isTurnover && isDefensivePenalty(play, penaltyFragment);
};

/**
 * Determine if the End of Play transition should be hidden
 * @param {string} ruleSetId
 * @param {Fragment} fragment
 * @param {FootballPlay} play
 * @returns {boolean}
 */
export const shouldHideEop = (ruleSetId, fragment, play) => {
    const isBlockedKick =
        fragment.associatedState === FootballState.FieldGoalBlocked;

    if (isBlockedKick) {
        const isPatPlay = play.historyContains(FootballState.PatTry);
        const isCfl = ruleSetId === Ruleset.Cfl.code;
        return !isCfl || !isPatPlay;
    }

    return false;
};

export const isRouge = (play, fragment, gameContext) => {
    const summaryContext = play.getEndContext(gameContext);
    const isKickPlay =
        play.historyContains(FootballState.Kick) ||
        play.historyContains(FootballState.Punt) ||
        play.historyContains(FootballState.CflKick);

    const isValidFragment =
        isStateFromTransition(
            fragment.associatedState,
            FootballTransition.Rouge,
            play
        ) || fragment.associatedState === FootballState.Rouge;

    if (isKickPlay && isValidFragment) {
        const { fieldPosition, homeTeamHasPossession } = summaryContext;

        return homeTeamHasPossession
            ? fieldPosition <= 0
            : fieldPosition >= gameContext.gameConfig.general.fieldLength;
    }

    return false;
};

export const isSafety = (play, fragment, gameContext) => {
    const summaryContext = play.getEndContext(gameContext);
    const safetyStates = [
        FootballState.Safety,
        FootballState.Tackle,
        FootballState.OutOfBounds,
        FootballState.FumbleRecovered,
        FootballState.FumbleNotRecovered,
        FootballState.PassSacked,
        FootballState.Continue
    ];

    if (safetyStates.includes(fragment.associatedState)) {
        let previousFragment = fragment;
        if (fragment.associatedState === FootballState.Safety) {
            previousFragment = play.getPreviousFragment(fragment);
        }

        const {
            tackledBy,
            recoveredBy,
            recoveredAt,
            sackedBy,
            sackedAt,
            continuedBy,
            continuedTo
        } = previousFragment;
        const [tacklingPlayer] = tackledBy || [sackedBy];
        const recoveringPlayer = recoveredBy || continuedBy;

        if (tacklingPlayer || recoveringPlayer) {
            let fieldPosition = 0;

            if (isNumber(recoveredAt)) {
                fieldPosition = recoveredAt;
            } else if (isNumber(sackedAt)) {
                fieldPosition = sackedAt;
            } else if (isNumber(continuedTo)) {
                fieldPosition = continuedTo;
            } else {
                fieldPosition = summaryContext.fieldPosition;
            }

            const isInOffense =
                !isPlayerInOffense(tacklingPlayer, summaryContext) ||
                isPlayerInOffense(recoveringPlayer, summaryContext);

            const isInOwnEndZone = isPositionInOwnEndZone(
                fieldPosition,
                summaryContext
            );
            return isInOffense && isInOwnEndZone;
        }
    }

    return false;
};

export const isOvertimePeriod = (period, maxPeriods) => {
    return period > maxPeriods;
};

export const getPeriodNumber = (period, maxPeriods) => {
    return period <= maxPeriods ? period : period - maxPeriods;
};

export const getPeriodAbbreviation = (periodNumber, maxPeriods) => {
    return periodNumber <= maxPeriods ? "QTR" : "OT";
};

export const getTimeoutNumber = (teamId, periodNumber, gameContext) => {
    let timeoutNumber;
    const halfNumber = halfNumberByPeriod(periodNumber, gameContext);
    if (halfNumber) {
        timeoutNumber = getTimeoutsByHalf(teamId, halfNumber, gameContext);
    } else {
        // Overtime
        timeoutNumber = getTimeoutsByPeriod(teamId, periodNumber, gameContext);
    }

    return timeoutNumber;
};

export const getPeriodSummary = (periodNumber, maxPeriod) => {
    return {
        period: getPeriodNumber(periodNumber, maxPeriod),
        abbreviation: getPeriodAbbreviation(periodNumber, maxPeriod)
    };
};

/**
 *
 * @param {number} minutesByPeriod Total number of minutes per period
 * @returns The seconds that the period should begin at
 */
export const getPeriodStartSeconds = (minutesByPeriod = 15) => {
    return minutesByPeriod * 60;
};

/**
 * Get the adjusted kick spot for a Good Field Goal
 * @param {FootballGameContext} context
 * @param {number} kickedTo
 * @returns {number}
 */
export const getGoodFgKickedToSpot = (context, kickedTo) => {
    const { gameConfig, homeTeamHasPossession } = context || {};
    const { endZoneLength } = gameConfig?.general || {};
    const { goalPostLocation } = gameConfig?.spots || {};
    let kickSpot = kickedTo;
    const excludeEndzone = goalPostLocation === GoalPost.GOAL_LINE.code;
    if (!excludeEndzone && endZoneLength) {
        kickSpot += homeTeamHasPossession ? endZoneLength : -endZoneLength;
    }
    return kickSpot;
};

/**
 *
 * @param {Boolean} isKickoff
 * @param {Boolean} homePossession
 * @param {any} gameConfig
 * @returns Touchback yardline based on possession and kickoff status
 */
export const getTouchbackYardline = (isKickoff, homePossession, gameConfig) => {
    const { touchbackYardline, KOTouchback } = gameConfig.spots;
    const { fieldLength } = gameConfig.general;

    const tbYardline = isKickoff ? KOTouchback : touchbackYardline;

    return homePossession ? tbYardline : fieldLength - tbYardline;
};

/**
 *
 * @param {Boolean} homePossession
 * @param {any} gameConfig
 * @returns Rouge yardline based on possession
 */
export const getRougeYardline = (homePossession, gameConfig) => {
    const { rougeYardline } = gameConfig.spots;
    const { fieldLength } = gameConfig.general;

    return homePossession ? rougeYardline : fieldLength - rougeYardline;
};

/**
 *
 * @param {*} context
 * @param {Number} fieldPosition Position to calculate gained yards with
 * @returns
 */
export const getYardsGained = (context, fieldPosition) => {
    return fieldPosition
        ? (fieldPosition - context.fieldPosition) *
              getFieldDirectionMultiplier(context)
        : 0;
};

export const getPatComplete = (context, contextSnapshot, play) => {
    const { homeTeamHasPossession: homeTeamPat } = contextSnapshot;
    const { fieldPosition, homeTeamHasPossession, gameConfig } = context;
    const { fieldLength } = gameConfig.general;
    const isInZone = homeTeamHasPossession
        ? fieldPosition >= fieldLength
        : fieldPosition <= 0;
    const isGoodKick = play.historyContains(FootballState.FieldGoalGood);
    const isAcceptedPenalty = play.historyContains(
        FootballState.PenaltyAccepted
    );

    const failedKickFragment =
        play.getFirstFragment(FootballState.FieldGoalMissed) ||
        play.getFirstFragment(FootballState.FieldGoalBlocked);

    const isBallDead =
        failedKickFragment &&
        play
            .getFragmentsBetween(failedKickFragment, [])
            .every(({ associatedState }) =>
                [
                    failedKickFragment.associatedState,
                    FootballState.EndOfPlay,
                    FootballState.Penalty,
                    FootballState.PenaltyDeclined,
                    FootballState.SecondPenalty,
                    FootballState.Offsetting
                ].some((s) => s === associatedState)
            );

    return (
        !isAcceptedPenalty &&
        !isBallDead &&
        ((homeTeamPat === homeTeamHasPossession && isInZone) || isGoodKick)
    );
};
